import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/products/shared/state/auth-context';
import { listVerifiedTotpFactors } from '@/products/shared/utils/mfa';
import {
  fetchSessionTenantMfaEnabled,
  fetchTenantMfaEnabled,
  isMfaExemptRole,
  MFA_ENABLED_CONFIG_KEY,
  setTenantMfaEnabled,
} from '@/products/shared/utils/tenantMfa';

export const TENANT_MFA_QUERY_KEY = ['tenant_config', MFA_ENABLED_CONFIG_KEY] as const;
export const MFA_FACTORS_QUERY_KEY = ['mfa-totp-factors'] as const;

export const useTenantMfaEnabled = () => {
  const { user, tenantId } = useAuth();

  const query = useQuery({
    queryKey: [...TENANT_MFA_QUERY_KEY, user?.id, tenantId],
    queryFn: async () => {
      // Prefer auth-context tenant when present; otherwise resolve from session/JWT/user_roles.
      if (tenantId != null) {
        return fetchTenantMfaEnabled(tenantId);
      }
      return fetchSessionTenantMfaEnabled();
    },
    // Run whenever we have a logged-in user — do not wait solely on JWT tenant claim.
    enabled: Boolean(user),
    staleTime: 15_000,
  });

  const statusKnown = typeof query.data === 'boolean';

  return {
    mfaEnabled: query.data === true,
    // Stay loading until we have a definitive boolean (do not treat error as "MFA off").
    isLoading: Boolean(user) && !statusKnown && !query.isError,
    error: query.error instanceof Error ? query.error.message : null,
    refetch: query.refetch,
  };
};

/**
 * Tenant MFA flag with role exemptions applied. Gates should use this;
 * useTenantMfaEnabled stays the raw tenant setting for the admin toggle.
 */
export const useMfaRequired = () => {
  const { userRole } = useAuth();
  const { mfaEnabled, isLoading, error, refetch } = useTenantMfaEnabled();
  const exempt = isMfaExemptRole(userRole);

  return {
    mfaRequired: mfaEnabled && !exempt,
    isLoading: exempt ? false : isLoading,
    error: exempt ? null : error,
    refetch,
  };
};

export const useUpdateTenantMfaEnabled = () => {
  const { tenantId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (enabled: boolean) => {
      if (tenantId == null) {
        throw new Error('Tenant ID is required');
      }
      await setTenantMfaEnabled(tenantId, enabled);
      return enabled;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: TENANT_MFA_QUERY_KEY });
    },
  });
};

/** When tenant MFA is on, true if the user has no verified authenticator app yet. */
export const useMfaEnrollmentRequired = (mfaEnabled: boolean) => {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: [...MFA_FACTORS_QUERY_KEY, user?.id, 'enrollment-required'],
    queryFn: async () => {
      const { data, error } = await listVerifiedTotpFactors();
      if (error) {
        throw new Error(error.message);
      }
      return data.length === 0;
    },
    enabled: Boolean(user) && mfaEnabled,
    staleTime: 10_000,
  });

  return {
    // Fail closed: treat load errors as needing enrollment so users cannot skip MFA.
    needsEnrollment: mfaEnabled && (query.data === true || Boolean(query.error)),
    isLoading: mfaEnabled && Boolean(user) && query.isLoading,
    error: query.error instanceof Error ? query.error.message : null,
    refetch: query.refetch,
  };
};
